import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ErrorMessage from '@components/ErrorMessage'
import InputText from '@components/formControls/InputText'
import ClearIcon from '@mui/icons-material/Clear'
import { Divider, Radio } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import { TypographyVariant as Variant } from '@mui/material/styles/createTypography'

import { ButtonContainer, SmallTextMessage } from './index.styles'

export interface InputWithTitleSectionProps {
  title: string
  label: string
  onCancel: (value: string) => boolean | void
  validate?: (value: string) => string | undefined
  onSubmit?: (value: string, cb: () => void, error?: string) => void
  onChange?: (value: string) => void
  submitButtonText?: string
  initialValue?: string
  maxLength?: number
  multiline?: boolean
  middleButtonOnClick?: (cb: () => void) => void
  middleButtonText?: string
  allowEmpty?: boolean
  inputRefToParent?: (ref: Nullable<HTMLInputElement>) => void
  children?: ReactNode
  isSubmitButtonEnabled?: boolean
  subTitle?: string
  selectable?: boolean
  checked?: boolean
  onSelect?: () => void
  showMaxLengthMessage?: boolean
  value?: string
  hideInput?: boolean
  subtitleWithDivider?: boolean
  subtitleVariant?: Variant
  submitWithError?: boolean
  errorRenderer?: (error: string) => ReactNode
  onClear?: () => void
}

const InputWithTitleSection = (props: InputWithTitleSectionProps) => {
  const { t } = useTranslation()
  const {
    title,
    label,
    submitButtonText,
    initialValue,
    maxLength,
    multiline,
    middleButtonText,
    middleButtonOnClick,
    validate,
    onSubmit,
    onChange,
    onCancel,
    onClear,
    allowEmpty = false,
    inputRefToParent,
    children,
    isSubmitButtonEnabled,
    subTitle,
    selectable,
    checked,
    onSelect,
    showMaxLengthMessage,
    value: incomingValue,
    hideInput,
    subtitleWithDivider = true,
    subtitleVariant = 'caption',
    submitWithError = false,
    errorRenderer,
  } = props
  const [validationError, setValidationError] = useState('')
  const [editedValue, setEditedValue] = useState(incomingValue ?? '')
  const inputRef = useRef<Nullable<HTMLInputElement>>(null)

  useEffect(() => {
    setEditedValue(initialValue ?? incomingValue ?? '')
    inputRef.current?.focus?.()
    inputRefToParent?.(inputRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !submitButtonDisabled()) {
      event.preventDefault()
      submitValue()
    }
  }

  const handleValueChange = (value: string) => {
    if (maxLength && value?.length > maxLength) {
      return
    }
    setEditedValue(value)
    onChange?.(value)
    setValidationError('')
  }

  const handleCancelClick = () => {
    const closed = onCancel?.(editedValue)

    if (closed) {
      handleValueChange('')
    }
  }

  const submitValue = () => {
    const error = validate?.(editedValue)

    if (error) {
      setValidationError(error)
      if (submitWithError) {
        onSubmit?.(editedValue, () => setEditedValue(''), error)
      }
      return
    }

    setValidationError('')
    onSubmit?.(editedValue, () => setEditedValue(''))
  }

  const submitButtonDisabled = () => {
    if (isSubmitButtonEnabled ?? false) {
      return false
    }

    if (initialValue === editedValue.trim()) {
      return true
    }

    if (!allowEmpty) {
      return !editedValue
    }

    return false
  }

  const handleClearClick = () => {
    handleValueChange('')
    onClear?.()
  }

  const errorComponent = useMemo(
    () => (errorRenderer ? errorRenderer(validationError) : <ErrorMessage error={validationError} />),
    [validationError, errorRenderer],
  )

  return (
    <>
      <Box mb="20px">
        <Typography variant="h2">{title}</Typography>
        {subTitle && (
          <Box mb="16px" mt="6px">
            <Typography variant={subtitleVariant || 'caption'}>{subTitle}</Typography>
            {subtitleWithDivider ? <Divider sx={{ margin: '10px 0' }} /> : null}
          </Box>
        )}
      </Box>
      {!hideInput && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <InputText
            multiline={multiline}
            inputRef={inputRef}
            value={editedValue}
            onChange={e => handleValueChange(e?.target?.value)}
            onKeyPress={onKeyPress}
            inputProps={{
              height: 'auto',
              'data-testid': 'input_with_title_section_input',
            }}
            InputLabelProps={{
              shrink: true,
            }}
            label={label}
            // eslint-disable-next-line
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {editedValue ? (
                    <IconButton onClick={handleClearClick} data-testid="input_with_title_section_clear_input_icon">
                      <ClearIcon htmlColor="rgba(0, 0, 0, 0.54)" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
            fullWidth
          />
          {selectable && <Radio disableRipple checked={checked} onClick={onSelect} />}
        </Box>
      )}
      {showMaxLengthMessage !== false && maxLength && !validationError && (
        <SmallTextMessage>
          {maxLength - editedValue.length === 1
            ? t(translationKeys.CHARACTER_LEFT, { count: maxLength - editedValue.length })
            : t(translationKeys.CHARACTERS_LEFT, { count: maxLength - editedValue.length })}
        </SmallTextMessage>
      )}
      {errorComponent}
      {children}
      <ButtonContainer>
        <Button variant="outlined" onClick={handleCancelClick} data-testid="input_with_title_section_cancel_btn">
          {t(translationKeys.CANCEL_BUTTON)}
        </Button>

        {middleButtonText && (
          <Button
            variant="outlined"
            onClick={() => middleButtonOnClick?.(() => setEditedValue(''))}
            data-testid="input_with_title_section_middle_btn"
          >
            {middleButtonText}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={submitValue}
          disabled={submitButtonDisabled()}
          data-testid="input_with_title_section_submit_btn"
        >
          {submitButtonText || t(translationKeys.CONFIRM_BUTTON)}
        </Button>
      </ButtonContainer>
    </>
  )
}

export default InputWithTitleSection
